/**
 * Voice-command cheat sheet for the Performance Metrics region.
 *
 * Mounted above ActivityAccordion in BallByBallPage. Collapsed by
 * default so it doesn't push the chip grid down for power users; one
 * tap shows the full vocabulary. Examples are deliberately phrased as
 * an operator would naturally speak them ("middle stump, off break,
 * big turn") so the muscle memory matches the SPA's parser.
 *
 * The phrases here MUST stay aligned with the patterns in
 * src/hooks/useVoiceScoring.js — if a phrase shown to the operator
 * isn't actually parsed, that's a UX regression. Whenever you add /
 * remove a vocab in useVoiceScoring, update the corresponding section
 * here too.
 */
import { useState } from "react";
import { Mic, ChevronDown, ChevronUp } from "lucide-react";
import VoicePlayButton from "./VoicePlayButton";


// One row per metric group. Each row: label + concrete example phrases
// the operator can SAY to set the field.
const GROUPS = [
  {
    title: "Keeper",
    examples: ["standard", "up to stumps", "leg side shift", "off side shift"],
  },
  {
    title: "Bounce",
    examples: ["extra bounce", "standard", "kept low", "variable bounce"],
  },
  {
    title: "Delivery type — pace",
    examples: [
      "in swinger", "out swinger", "off cutter", "leg cutter",
      "slower ball", "knuckle ball", "seam in", "seam away",
      "angled in", "angled away", "back of hand",
    ],
  },
  {
    title: "Delivery type — spin",
    examples: [
      "off break", "leg spinner", "googly", "doosra", "carrom ball",
      "arm ball", "top spinner", "slider", "flipper", "quicker ball",
      "left arm orthodox",
    ],
  },
  {
    title: "Spin direction",
    examples: ["off spin", "leg spin", "top spin", "slider", "wrong un", "carrom", "doosra"],
  },
  {
    title: "Drift",
    examples: ["drift in", "drift away", "no drift"],
  },
  {
    title: "Turn",
    examples: ["big turn", "small turn", "straight on", "skid"],
  },
  {
    title: "Stroke",
    examples: [
      "cover drive", "straight drive", "off drive", "on drive", "square drive",
      "pull", "hook", "sweep", "reverse sweep", "slog sweep", "paddle sweep",
      "scoop", "cut", "late cut", "upper cut", "flick", "glance", "switch hit",
      "forward defence", "backward defence", "leave", "padded",
    ],
  },
  {
    title: "Foot movement",
    examples: [
      "front foot", "back foot", "down the track", "backs away",
      "swayed away", "ducked", "deep in crease", "no movement",
    ],
  },
  {
    title: "Shot connection",
    examples: [
      "middled", "well timed", "mis-timed", "outside edge", "inside edge",
      "top edge", "leading edge", "thick edge", "play and miss", "beaten",
      "shoulder arms", "bat pad", "padded", "gloved", "hit pad", "hit body",
    ],
  },
  {
    title: "Air movement",
    examples: ["along the ground", "flat", "loopy", "high in the air"],
  },
  {
    title: "Control",
    examples: ["controlled", "uncontrolled", "in control", "lost control"],
  },
  {
    title: "Pitch matrix (length + line in one phrase)",
    examples: [
      "yorker outside off", "good length on middle", "back of a length leg stump",
      "short outside leg", "full toss on the stumps",
    ],
  },
  {
    title: "Commentary — free text (use the prefix)",
    examples: [
      'commentary great shot down the ground',
      'note keeper missed a stumping chance',
      'comment edged short of slip',
      'remark thunderous strike over long-on',
    ],
  },
];


// localStorage flag — operator preference persists across sessions.
// 2026-05-27: defaults to COLLAPSED (was: auto-open on first visit)
// per founder feedback that the cheat sheet pushes the chip grid
// down too much on tablet / phone. Power users can still expand it
// once and it stays open across sessions.
const STORAGE_KEY = "zcd_metrics_voice_help_open";

function initialOpen() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === null) return false;
    return v === "1";
  } catch { return false; }
}


export default function VoiceCommandsHelp() {
  const [open, setOpen] = useState(initialOpen);
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  return (
    <div className={`rounded-md border ${open ? "border-purple-400/60 bg-purple-500/10" : "border-purple-400/30 bg-purple-500/5"} text-[11px]`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-purple-100 hover:text-white transition-colors"
        title="Voice command cheat sheet for performance metrics — tap any phrase 🔊 to hear it"
      >
        <span className="flex items-center gap-1.5">
          <Mic className="w-3.5 h-3.5 text-purple-300" />
          <span className="uppercase tracking-wider text-[10px] font-semibold">
            🎓 Voice — Performance Metrics
          </span>
          <span className="px-1.5 py-0.5 rounded bg-purple-400/30 text-purple-100 text-[9px] uppercase tracking-wider">
            tap 🔊 to hear
          </span>
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="px-2.5 pb-2 space-y-1.5 max-h-[40vh] overflow-y-auto">
          <p className="text-[10px] text-text-secondary leading-snug">
            With the mic on, say any of the phrases below. A single utterance can set
            multiple metrics — e.g. <em>"off break, big turn, played to cover, middled,
            flat"</em> sets delivery, turn, wagon position, connection, and air.
          </p>
          <p className="text-[10px] text-purple-200 leading-snug border-l-2 border-purple-400/60 pl-2">
            <strong>Tip — lock the scope:</strong> prefix the phrase with the
            field name to avoid cross-matches.{" "}
            <em>"stroke no shot"</em> only touches Stroke (won't fire pitch=Short).{" "}
            <em>"keeper standard"</em>, <em>"delivery slider"</em>,{" "}
            <em>"bounce kept low"</em>, <em>"connection middled"</em> all stay
            inside their named field.
          </p>
          {GROUPS.map((g) => (
            <div key={g.title} className="border-t border-bg-secondary/60 pt-1">
              <div className="text-[10px] uppercase tracking-wider text-purple-300 font-medium mb-0.5">
                {g.title}
              </div>
              <div className="flex flex-wrap gap-1">
                {g.examples.map((ex) => (
                  <span
                    key={ex}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-bg-secondary/60 rounded text-text-secondary text-[10px] font-mono"
                  >
                    "{ex}"
                    <VoicePlayButton phrase={ex} size="xs" />
                  </span>
                ))}
              </div>
            </div>
          ))}
          <div className="border-t border-bg-secondary/60 pt-1 text-[10px] text-text-secondary leading-snug">
            <strong className="text-text-primary">Tips:</strong> say <em>"save"</em> /{" "}
            <em>"next ball"</em> to record, <em>"undo"</em> to revert, <em>"cancel"</em> to
            abort a pending save. Player swaps: <em>"next bowler bumrah"</em>,{" "}
            <em>"kohli on strike"</em>, <em>"keeper urvil"</em>.
          </div>
        </div>
      )}
    </div>
  );
}
